/* eslint-disable complexity */
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@ils/osces/client/ils-osce-type.js';
import { hasRole } from '@ils/permissions/hasRole.js';
import { subscribeSlotsForUser } from '@ils/slots/api/slot.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { UserRole } from '@ils/static/enums.js';
import type { ILSThemeDefinition } from '@ils/theme/theme.js';
import { html, noChange, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { isMobileSize } from '../ils-window.sizes.js';
import './ils-loader.js';

@customElement('ils-timeline')
export class ILSTimeline extends ChemistryLitElement<ILSThemeDefinition> {
  @state() public examSlots = new RXController(
    this,
    routingState.searchParams.get('day').pipe(
      switchMap(day => {
        if (day)
          return subscribeSlotsForUser(day)({
            startTime: 1,
            endTime: 1,
            isCandidateMissing: 1,
            osce: { titleTrainee: 1, enableVideo: 1, taskType: 1, osceType: 1, conversationType: 1 },
          });
        return of([] as Slot[]);
      }),
      map(slots => {
        return slots.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
      }),
    ),
  );
  @state() public location = new RXController(
    this,
    routingState.searchParams.get('day').pipe(
      switchMap(day => {
        if (day)
          return getByID(
            ExamDay,
            day,
          )({
            location: {
              address: {
                streetAndHousenumber: 1,
                name: 1,
                zipCity: { zip: 1, city: 1 },
                bundesland: 1,
                country: 1,
                additionalInfo: 1,
              },
            },
          });
        return of(null);
      }),
      map(day => day?.location),
    ),
  );
  @state() isMobile = new RXController(this, isMobileSize);
  @state() isTrainee = new RXController(
    this,
    currentUser<ILSUser>().pipe(
      filter(Boolean),
      map(user => hasRole(user, UserRole.TRAINEE)),
    ),
  );

  override render() {
    if (this.examSlots.isLoading)
      return html`
        <x-flex center crossaxis-center ${css({ width: '100%', height: '100%' })}>
          <ils-loader></ils-loader>
        </x-flex>
      `;
    if (!this.examSlots.value) return noChange;
    if (!this.examSlots.value[0])
      return html`
        <x-empty-placeholder
          .text=${'Ihre genauen Prüfungszeiten stehen derzeit noch nicht fest. Sie erhalten die genaue Uhrzeit kurz vor der Prüfung!'}
          .img=${'alarm'}
          no-wrapper
        ></x-empty-placeholder>
      `;

    const numColumns = this.isMobile.value ? 'min-content max-content' : 'repeat(3, max-content)';
    const early = this.isTrainee.value ? 60 : 30;
    return html`
      <x-flex space="lg" padding="md">
        <x-grid columns=${numColumns} space="md" ${css({ alignItems: 'center' })}>
          <x-text
            bold
            tone=${this.examSlots.value[0]!.startTime <= DateTime.now() ? 'subtle' : ''}
            ${css({ gridColumn: this.isMobile.value ? '1 / -1' : '' })}
          >
            ${this.examSlots.value[0]!.startTime.minus({ minute: early }).toLocaleString(DateTime.TIME_24_SIMPLE)}
          </x-text>
          <x-flex>
            <x-icon ${css({ color: this.colors.role.doctor })}> train </x-icon>
          </x-flex>
          <x-text tone=${this.examSlots.value[0].startTime < DateTime.now() ? 'subtle' : ''}>
            Ankunft: ${this.location.value?.address?.toString() ?? ''}
          </x-text>
          ${this.isTrainee.value
            ? html` <x-text tone="subtle" ${css({ gridColumn: '1 / -1' })}> Prüfung Ihrer Ausweisdokumente </x-text> `
            : html`
                <x-flex space="xs" ${css({ gridColumn: '1 / -1' })}>
                  <x-text tone="subtle"> Testen der Internetverbindung und warten auf Prüfung </x-text>
                  <x-button
                    @click=${() =>
                      goTo({
                        path: '/start/check-in',
                        searchParams: NO_CHANGE,
                      })}
                  >
                    Zum Testraum
                  </x-button>
                </x-flex>
              `}
          ${repeat(
            this.examSlots.value,
            slot => slot._id,
            (slot, index) => this.slotElement(slot, index),
          )}
        </x-grid>
      </x-flex>
    `;
  }

  private slotElement(slot: Slot, index: number) {
    const last = this.examSlots.value![index - 1];
    const now = DateTime.now();
    if (!slot.endTime || (last && !last.endTime)) throw new Error('slot or last .endTime is undefined');
    const interval = slot.startTime.until(slot.endTime);
    const pauseInterval = last ? last.endTime!.until(slot.startTime) : null;
    const isActive = interval.contains(now);
    const isPast = slot.endTime! < DateTime.now();

    const textColor = isPast ? 'subtle' : slot.isCandidateMissing ? 'error' : '';

    return html`
      ${pauseInterval
        ? html`
            <x-flex ${css({ gridColumn: '1 / -1' })}>
              <x-text tone="subtle">
                ${pauseInterval.toDuration('minutes').minutes.toFixed(0)} Minuten Pause von
                ${pauseInterval.start?.toLocaleString(DateTime.TIME_SIMPLE) ?? '-'} bis
                ${pauseInterval.end?.toLocaleString(DateTime.TIME_SIMPLE) ?? '-'}
              </x-text>
            </x-flex>
          `
        : nothing}

      <x-flex horizontal crossaxis-center ${css({ gridColumn: this.isMobile.value ? '1 / -1' : '' })}>
        <x-text bold tone=${isPast ? 'subtle' : ''}>
          ${interval.start?.toLocaleString(DateTime.TIME_SIMPLE) ?? '-'} -
          ${interval.end?.toLocaleString(DateTime.TIME_SIMPLE) ?? '-'}
        </x-text>
      </x-flex>
      <ils-osce-type .osce=${slot.osce} ${css({ gridColumn: this.isMobile.value ? '1' : '' })}> </ils-osce-type>
      <x-flex horizontal crossaxis-center space="sm">
        <x-text tone=${textColor}> ${slot.osce?.titleTrainee} </x-text>
        ${isActive
          ? html`
              <x-button
                mode="icon"
                icon="play_arrow"
                @click=${() =>
                  goTo({
                    path: this.isTrainee.value ? '/exam' : '/start/exam',
                    searchParams: NO_CHANGE,
                  })}
              ></x-button>
            `
          : nothing}
      </x-flex>
    `;
  }
}
